import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  CheckCircle, AlertTriangle, FileText, Loader2, PenTool, Shield,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SupplierSigningPage() {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [form, setForm] = useState({ signer_name: "", signer_title: "", agreed: false });

  useEffect(() => {
    const fetchContract = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/signatures/public/${token}`);
        setContract(res.data);
        setForm(prev => ({ ...prev, signer_name: res.data.supplier_name || "" }));
      } catch (err) {
        const msg = err.response?.data?.detail || "Unable to load contract. The link may be invalid or expired.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    fetchContract();
  }, [token]);

  const handleSign = async () => {
    if (!form.signer_name.trim()) return;
    if (!form.agreed) return;
    setSigning(true);
    try {
      await axios.post(`${API_URL}/api/signatures/public/${token}/sign`, form);
      setSigned(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to sign. Please try again.");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 size={32} className="animate-spin text-slate-400" />
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-lg w-full text-center">
          <CardContent className="py-12 space-y-4">
            <CheckCircle size={48} className="mx-auto text-emerald-500" />
            <h1 className="text-xl font-bold text-slate-800">Contract Signed via DocuSign</h1>
            <p className="text-sm text-slate-500">
              The contract has been fully executed. A signed copy has been sent to your email via DocuSign for your records.
            </p>
            <p className="text-xs text-slate-400 italic mt-4">You may close this page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-lg w-full text-center">
          <CardContent className="py-12 space-y-4">
            <AlertTriangle size={48} className="mx-auto text-amber-500" />
            <h1 className="text-xl font-bold text-slate-800">Unable to Load Contract</h1>
            <p className="text-sm text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-8 h-8 bg-[#4C00FF] rounded flex items-center justify-center">
              <PenTool size={16} className="text-white" />
            </div>
            <span className="text-lg font-bold text-[#4C00FF]">DocuSign</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-800" data-testid="signing-page-title">
            Contract Counter-Signature
          </h1>
          <p className="text-sm text-slate-500">
            You have been invited to review and counter-sign the following contract via DocuSign.
          </p>
        </div>

        {/* Contract Summary */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <FileText size={16} /> Contract Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-slate-400 uppercase">Title</p>
                <p className="font-medium text-slate-800" data-testid="contract-title">{contract.title}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase">Type</p>
                <p className="text-slate-700">{contract.contract_type || "-"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase">Jurisdiction</p>
                <p className="text-slate-700">{contract.jurisdiction || "-"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase">Value</p>
                <p className="text-slate-700">{contract.contract_value ? `£${Number(contract.contract_value).toLocaleString()}` : "-"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase">Effective Date</p>
                <p className="text-slate-700">{contract.effective_date || "-"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase">Expiration Date</p>
                <p className="text-slate-700">{contract.expiration_date || "-"}</p>
              </div>
            </div>

            {/* DocuSign Envelope ID */}
            {contract.docusign_envelope_id && (
              <div className="pt-3 border-t">
                <div className="flex items-center gap-2 text-xs text-[#4C00FF]">
                  <PenTool size={12} />
                  <span>DocuSign Envelope: {contract.docusign_envelope_id}</span>
                </div>
              </div>
            )}

            {/* Internal Signatures */}
            {contract.internal_signatures?.length > 0 && (
              <div className="pt-3 border-t">
                <p className="text-xs text-slate-400 uppercase mb-2">Signed By</p>
                {contract.internal_signatures.map((sig, i) => (
                  <div key={sig.name || `sig-${i}`} className="flex items-center gap-2 text-sm">
                    <CheckCircle size={14} className="text-emerald-500" />
                    <span className="text-slate-700">{sig.name}</span>
                    <span className="text-xs text-slate-400">— {new Date(sig.signed_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contract Content */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">
              Contract Content
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none bg-white border rounded-sm p-6 max-h-[400px] overflow-y-auto text-sm leading-relaxed text-slate-700" data-testid="contract-content">
              {contract.sections?.length > 0 ? (
                contract.sections.map((section, i) => (
                  <div key={section.name || `section-${i}`} className="mb-4">
                    <h3 className="font-bold text-slate-800 mb-1">{section.name}</h3>
                    {section.content?.split("\n").map((p, j) => (
                      p.trim() && <p key={j} className="mb-1">{p}</p>
                    ))}
                  </div>
                ))
              ) : (
                contract.content?.split("\n").map((p, i) => (
                  p.trim() && <p key={i} className="mb-1">{p}</p>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Signing Form */}
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-wider text-emerald-700 flex items-center gap-2">
              <PenTool size={16} /> Sign via DocuSign
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase text-slate-500">Your Full Name *</Label>
                <Input
                  value={form.signer_name}
                  onChange={(e) => setForm({ ...form, signer_name: e.target.value })}
                  placeholder="e.g., John Smith"
                  className="rounded-sm"
                  data-testid="supplier-signer-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase text-slate-500">Title / Position</Label>
                <Input
                  value={form.signer_title}
                  onChange={(e) => setForm({ ...form, signer_title: e.target.value })}
                  placeholder="e.g., Chief Executive Officer"
                  className="rounded-sm"
                  data-testid="supplier-signer-title"
                />
              </div>
            </div>

            <div className="flex items-start gap-2 pt-2">
              <Checkbox
                id="agree"
                checked={form.agreed}
                onCheckedChange={(checked) => setForm({ ...form, agreed: checked })}
                data-testid="supplier-agree-checkbox"
              />
              <label htmlFor="agree" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
                I have reviewed the contract in its entirety and agree to be bound by its terms and conditions. I confirm I have the authority to sign on behalf of {contract.supplier_name || "the supplier"}.
              </label>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button
                onClick={handleSign}
                disabled={signing || !form.signer_name.trim() || !form.agreed}
                className="gap-2 rounded-sm bg-emerald-600 hover:bg-emerald-700"
                data-testid="supplier-sign-btn"
              >
                {signing ? <Loader2 size={16} className="animate-spin" /> : <PenTool size={16} />}
                Sign Contract
              </Button>
            </div>

            <div className="flex items-start gap-2 pt-2 text-xs text-slate-400">
              <Shield size={13} className="shrink-0 mt-0.5" />
              <p>Powered by DocuSign. Your signature is legally binding. This signing session is secured and tied to your email address.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
